import React, { useState, useEffect } from "react";
import {
  BiCheck,
  BiChevronUp,
  BiLoaderAlt,
  BiMessage,
  BiTrendingDown,
  BiTrendingUp,
  BiX,
} from "react-icons/bi";
import { BsActivity, BsClock, BsEye } from "react-icons/bs";
import { FiAlertTriangle } from "react-icons/fi";
import { connect } from "react-redux";
import { getEmailAnalytics } from "../../../../stores/actions/campaign.actions.types";
import EmailListModal from "../../../Modals/email-modal";

/* ---------------- ICON HELPER ---------------- */
const getIcon = (key) => {
  switch (key) {
    case "total_sent":
      return <BiMessage size={20} className='text-gray-500' />;
    case "delivered":
      return <BiCheck size={20} className='text-gray-500' />;
    case "delivery_rate":
      return <BsActivity size={20} className='text-gray-500' />;
    case "failed":
      return <BiX size={20} className='text-gray-500' />;
    case "unsubscribed":
      return <FiAlertTriangle size={20} className='text-gray-500' />;
    case "last_hour":
    case "last_12_hours":
      return <BsClock size={20} className='text-gray-500' />;
    default:
      return <div className='w-5 h-5' />;
  }
};

/* ---------------- MINI METRIC CARD ---------------- */
const MiniMetricCard = ({
  onClick,
  label,
  value,
  trend,
  trendDirection,
  secondaryText,
  status,
  statusColor,
  isSmall,
  iconKey,
}) => {
  let trendElement;
  let mainValueClass = isSmall ? "text-xl" : "text-2xl";

  if (trend !== undefined) {
    const TrendIcon = trendDirection === "up" ? BiTrendingUp : BiTrendingDown;
    const trendColor =
      trendDirection === "up"
        ? "text-green-500"
        : trendDirection === "down"
          ? "text-red-500"
          : "text-gray-400";

    trendElement = (
      <div className={`flex items-center text-sm font-medium ${trendColor}`}>
        <TrendIcon size={14} className='mr-1' />
        {typeof trend === "number" ? trend.toFixed(trend === 0 ? 3 : 2) : trend}
        {trendDirection !== "neutral" ? "%" : ""}
      </div>
    );
  } else if (status) {
    trendElement = (
      <div
        className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
          statusColor === "rate-bad"
            ? "bg-red-900/50 text-red-400"
            : "bg-green-900/50 text-green-400"
        }`}
      >
        {status}
      </div>
    );
    mainValueClass =
      "text-2xl font-extrabold " +
      (statusColor === "rate-bad" ? "text-red-400" : "text-[#58a6ff]");
  }

  return (
    <div
      onClick={onClick}
      className='bg-white dark:bg-boxdark border cursor-pointer  border-gray-200 hover:border-red-600  p-6 rounded-xl shadow-md flex flex-col justify-between h-full'
    >
      <div className='flex justify-between items-center mb-4'>
        {getIcon(iconKey)}
        {trendElement}
      </div>

      <div className='flex-grow'>
        <p className='text-sm font-medium text-gray-500 dark:text-gray-300 mb-1'>
          {label}
        </p>
        <div
          className={`font-bold text-gray-900 dark:text-gray-100 ${mainValueClass}`}
        >
          {value ?? 0}
        </div>
      </div>

      <p className='text-xs text-gray-500 dark:text-gray-300 mt-2'>
        {secondaryText ?? ""}
      </p>
    </div>
  );
};

/* ---------------- EXTENDED CARD ---------------- */
const ExtendedCard = ({
  onClick,
  label,
  value,
  context,
  secondaryText,
  icon,
}) => (
  <div
    onClick={onClick}
    className='bg-white  dark:bg-boxdark border cursor-pointer  border-gray-200  hover:border-red-600  p-6 rounded-xl shadow-md h-full'
  >
    <div className='flex justify-between items-center mb-4'>
      {icon}
      <div className='flex items-center text-sm font-medium text-red-500'>
        <BiChevronUp size={14} className='mr-1' />
        {context ?? ""}
      </div>
    </div>
    <p className='text-sm font-medium text-gray-500 dark:text-gray-300 mb-1'>
      {label}
    </p>
    <div className='text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1'>
      {value ?? 0}
    </div>
    <p className='text-xs text-gray-500 dark:text-gray-300 mt-2'>
      {secondaryText ?? ""}
    </p>
  </div>
);

/* ---------------- TRANSFORM METRICS ---------------- */
const transformMetrics = (statsArray) => {
  if (!Array.isArray(statsArray) || statsArray.length === 0) return null;

  const safeRate = (delivered = 0, total = 0) => {
    if (!total || total === 0) return "0%";
    return `${((delivered / total) * 100).toFixed(2)}%`;
  };

  const pickRateColor = (delivered = 0, total = 0) =>
    total === 0 || delivered / total < 0.9 ? "rate-bad" : "rate-good";

  const findRange = (range) =>
    statsArray?.find((s) => s?.range === range) ?? {};

  const lastHour = findRange("lastHour");
  const last12h = findRange("last12h");
  const last24h = findRange("last24h");
  const last5d = findRange("last5d");

  return {
    last_24_hours: {
      total_sent: {
        value: last24h?.counts?.accepted ?? 0,
        secondaryText: "Total messages sent",
      },
      delivered: {
        value: last24h?.counts?.delivered ?? 0,
        secondaryText: "Delivered messages",
      },
      delivery_rate: {
        value: safeRate(
          last24h?.counts?.delivered ?? 0,
          last24h?.counts?.accepted ?? 0,
        ),
        status:
          (last24h?.counts?.delivered ?? 0) /
            ((last24h?.counts?.accepted ?? 1) || 1) <
          0.9
            ? "Needs Work"
            : "Good",
        statusColor: pickRateColor(
          last24h?.counts?.delivered ?? 0,
          last24h?.counts?.accepted ?? 0,
        ),
        secondaryText: `${
          last24h?.counts?.delivered ?? 0
        } delivered successfully`,
      },
      failed: {
        value: last24h?.counts?.failed ?? 0,
        secondaryText: "Failure count",
      },
      unsubscribed: {
        value: last24h?.counts?.unsubscribed ?? 0,
        secondaryText: "Unsubscribed events",
      },
      last_hour: {
        value: lastHour?.counts?.accepted ?? 0,
        secondaryText: "Last hour volume",
      },
      last_12_hours: {
        value: last12h?.counts?.accepted ?? 0,
        secondaryText: "Last 12 hours volume",
      },
    },
    extended_period: {
      last_7_days_sent: {
        value: last5d?.counts?.accepted ?? 0,
        context: `+${last5d?.counts?.failed ?? 0} failed`,
        secondaryText: `Failure rate: ${safeRate(
          last5d?.counts?.failed ?? 0,
          last5d?.counts?.accepted ?? 0,
        )}`,
      },
      last_7_days_rate: {
        value: safeRate(
          last5d?.counts?.delivered ?? 0,
          last5d?.counts?.accepted ?? 0,
        ),
        context: `+${last5d?.counts?.delivered ?? 0} delivered`,
        secondaryText: `${last5d?.counts?.accepted ?? 0} sent total`,
      },
    },
    breakdown_data: [
      {
        period: "Last Hour",
        label: "lastHour",
        sent: lastHour?.counts?.accepted ?? 0,
        delivered: lastHour?.counts?.delivered ?? 0,
        failed: lastHour?.counts?.failed ?? 0,
        undelivered:
          (lastHour?.counts?.accepted ?? 0) -
          (lastHour?.counts?.delivered ?? 0),
        rate: safeRate(
          lastHour?.counts?.delivered ?? 0,
          lastHour?.counts?.accepted ?? 0,
        ),
        rateColor: pickRateColor(
          lastHour?.counts?.delivered ?? 0,
          lastHour?.counts?.accepted ?? 0,
        ),
      },
      {
        period: "Last 12 Hours",
        label: "last12h",
        sent: last12h?.counts?.accepted ?? 0,
        delivered: last12h?.counts?.delivered ?? 0,
        failed: last12h?.counts?.failed ?? 0,
        undelivered:
          (last12h?.counts?.accepted ?? 0) - (last12h?.counts?.delivered ?? 0),
        rate: safeRate(
          last12h?.counts?.delivered ?? 0,
          last12h?.counts?.accepted ?? 0,
        ),
        rateColor: pickRateColor(
          last12h?.counts?.delivered ?? 0,
          last12h?.counts?.accepted ?? 0,
        ),
      },
      {
        period: "Last 24 Hours",
        label: "last24h",
        sent: last24h?.counts?.accepted ?? 0,
        delivered: last24h?.counts?.delivered ?? 0,
        failed: last24h?.counts?.failed ?? 0,
        undelivered:
          (last24h?.counts?.accepted ?? 0) - (last24h?.counts?.delivered ?? 0),
        rate: safeRate(
          last24h?.counts?.delivered ?? 0,
          last24h?.counts?.accepted ?? 0,
        ),
        rateColor: pickRateColor(
          last24h?.counts?.delivered ?? 0,
          last24h?.counts?.accepted ?? 0,
        ),
      },
      {
        period: "Last 5 Days",
        label: "last5d",
        sent: last5d?.counts?.accepted ?? 0,
        delivered: last5d?.counts?.delivered ?? 0,
        failed: last5d?.counts?.failed ?? 0,
        undelivered:
          (last5d?.counts?.accepted ?? 0) - (last5d?.counts?.delivered ?? 0),
        rate: safeRate(
          last5d?.counts?.delivered ?? 0,
          last5d?.counts?.accepted ?? 0,
        ),
        rateColor: pickRateColor(
          last5d?.counts?.delivered ?? 0,
          last5d?.counts?.accepted ?? 0,
        ),
      },
    ],
  };
};

/* ---------------- EMAIL ANALYTICS COMPONENT ---------------- */
const EmailAnalytics = ({ getEmailAnalytics }) => {
  const [metrics, setMetrics] = useState(null);
  const [rawMetrics, setRawMetrics] = useState(null);
  const [selected, setSelected] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getEmailAnalytics?.({
      callback: (res) => {
        const transformed = transformMetrics(res?.data?.stats);
        setMetrics(transformed);
        setRawMetrics(res?.data?.stats);
        setLoading(false);
      },
    });
  }, [getEmailAnalytics]);

  const handleClickedData = (e) => {
    const { label, status } = e;
    const periodData = rawMetrics?.find((item) => item.range === label);

    if (!periodData) {
      console.warn("No data found for label:", label);
      return;
    }

    let messages = [];

    switch (status) {
      case "delivered":
        messages = periodData.messages.delivered.map((msg) => ({
          ...msg,
          status: "Delivered",
        }));
        break;

      case "failed":
        messages = periodData.messages.failed.map((msg) => ({
          ...msg,
          status: "Failed",
        }));
        break;

      case "unsubscribe":
        messages = periodData.messages.unsubscribed.map((msg) => ({
          ...msg,
          status: "Unsubscribe",
        }));
        break;

      case "total_sent":
        messages = [
          ...periodData.messages.delivered.map((msg) => ({
            ...msg,
            status: "Delivered",
          })),
          ...periodData.messages.failed.map((msg) => ({
            ...msg,
            status: "Failed",
          })),
          ...periodData.messages.unsubscribed.map((msg) => ({
            ...msg,
            status: "Unsubscribe",
          })),
        ];
        break;
      default:
        console.warn("Unknown status:", status);
    }

    if (messages?.length) {
      setSelected(messages);
      setModalOpen(true);
    }
  };

  const renderBreakdownTable = () => {
    if (!metrics?.breakdown_data || loading) {
      return (
        <tr>
          <td colSpan='6' className='py-4 text-center text-gray-500'>
            {loading ? "Fetching data..." : "No breakdown data available."}
          </td>
        </tr>
      );
    }

    return metrics.breakdown_data.map((row, idx) => (
      <tr
        key={idx}
        className='hover:bg-gray-50 dark:hover:bg-gray-900 transition duration-150'
      >
        <td className='px-6 py-3 text-sm text-gray-900 dark:text-gray-100'>
          {row?.period}
        </td>
        <td className='px-6 py-3 text-sm'>{row?.sent}</td>
        <td className='px-6 py-3 text-sm'>{row?.delivered}</td>
        <td className='px-6 py-3 text-sm'>{row?.failed}</td>
        <td className='px-6 py-3 text-sm'>{row?.undelivered}</td>
        <td
          className={`px-6 py-3 text-sm font-semibold ${
            row?.rateColor === "rate-good" ? "text-green-600" : "text-red-600"
          }`}
        >
          {row?.rate}
        </td>
        <td className='px-6 py-3 text-sm text-end'>
          <BsEye
            className='text-lg cursor-pointer'
            onClick={() =>
              handleClickedData({
                label: row?.label,
                status: "total_sent",
              })
            }
          />
        </td>
      </tr>
    ));
  };

  return (
    <div>
      {loading ? (
        <div className='flex rounded dark:bg-boxdark bg-white items-center justify-center min-h-96'>
          <BiLoaderAlt className='animate-spin text-4xl text-primary dark:text-white' />
        </div>
      ) : (
        <div className='min-h-screen p-3 bg-gray-50 dark:bg-gray-800 font-sans text-gray-800 dark:text-gray-200'>
          <div className='dashboard-content'>
            <header className='mb-8 border-b border-gray-200 dark:border-gray-700 pb-4'>
              <h1 className='text-2xl md:text-3xl font-bold mb-1'>
                Email Marketing Dashboard
              </h1>
              <p className='text-md text-gray-500 dark:text-gray-400'>
                Monitor campaign performance and delivery metrics via Mailgun.
              </p>
            </header>

            {!loading && metrics && (
              <>
                {/* Last 24 Hours */}
                <section className='mb-10'>
                  <h2 className='text-xl font-semibold mb-4'>Last 24 Hours</h2>
                  <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4'>
                    <MiniMetricCard
                      label='Total Sent'
                      iconKey='total_sent'
                      {...metrics?.last_24_hours?.total_sent}
                      onClick={() =>
                        handleClickedData({
                          label: "last24h",
                          status: "total_sent",
                        })
                      }
                    />
                    <MiniMetricCard
                      label='Delivered'
                      iconKey='delivered'
                      {...metrics?.last_24_hours?.delivered}
                      onClick={() =>
                        handleClickedData({
                          label: "last24h",
                          status: "delivered",
                        })
                      }
                    />
                    <MiniMetricCard
                      label='Delivery Rate'
                      iconKey='delivery_rate'
                      {...metrics?.last_24_hours?.delivery_rate}
                    />
                    <MiniMetricCard
                      label='Failed'
                      iconKey='failed'
                      {...metrics?.last_24_hours?.failed}
                      onClick={() =>
                        handleClickedData({
                          label: "last24h",
                          status: "failed",
                        })
                      }
                    />
                    <MiniMetricCard
                      label='Unsubscribe (24h)'
                      iconKey='unsubscribed'
                      {...metrics?.last_24_hours?.unsubscribed}
                      onClick={() =>
                        handleClickedData({
                          label: "last24h",
                          status: "unsubscribed",
                        })
                      }
                    />
                  </div>

                  <div className='grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3 gap-4 mt-4'>
                    <MiniMetricCard
                      label='Delivery Rate (24h)'
                      iconKey='delivery_rate'
                      {...metrics?.last_24_hours?.delivery_rate}
                    />
                    <MiniMetricCard
                      label='Last Hour'
                      iconKey='last_hour'
                      isSmall
                      {...metrics?.last_24_hours?.last_hour}
                      onClick={() =>
                        handleClickedData({
                          label: "lastHour",
                          status: "total_sent",
                        })
                      }
                    />
                    <MiniMetricCard
                      label='Last 12 Hours'
                      iconKey='last_12_hours'
                      isSmall
                      {...metrics?.last_24_hours?.last_12_hours}
                      onClick={() =>
                        handleClickedData({
                          label: "last12h",
                          status: "total_sent",
                        })
                      }
                    />
                  </div>
                </section>

                {/* Extended Period */}
                <section className='mb-10'>
                  <h2 className='text-xl font-semibold mb-4'>
                    Extended Period Overview
                  </h2>
                  <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4'>
                    <ExtendedCard
                      label='Last 5 Days - Sent'
                      icon={<BiMessage size={20} className='text-gray-500' />}
                      {...metrics?.extended_period?.last_7_days_sent}
                      onClick={() =>
                        handleClickedData({
                          label: "last5d",
                          status: "total_sent",
                        })
                      }
                    />
                    <ExtendedCard
                      label='Last 5 Days - Delivery Rate'
                      icon={<BsActivity size={20} className='text-gray-500' />}
                      {...metrics?.extended_period?.last_7_days_rate}
                    />
                  </div>
                </section>

                {/* Breakdown Table */}
                <section className='mt-12'>
                  <h2 className='text-xl font-semibold mb-4'>
                    All Periods Breakdown
                  </h2>
                  <div className='overflow-x-auto bg-white dark:bg-gray-800 border-gray-200 rounded-xl shadow-md'>
                    <table className='min-w-full divide-y divide-gray-200'>
                      <thead className='bg-gray-50 dark:bg-gray-900'>
                        <tr>
                          <th className='px-6 py-3 text-left text-xs font-medium uppercase'>
                            Period
                          </th>
                          <th className='px-6 py-3 text-left text-xs font-medium uppercase'>
                            Total Sent
                          </th>
                          <th className='px-6 py-3 text-left text-xs font-medium uppercase'>
                            Delivered
                          </th>
                          <th className='px-6 py-3 text-left text-xs font-medium uppercase'>
                            Failed
                          </th>
                          <th className='px-6 py-3 text-left text-xs font-medium uppercase'>
                            Undelivered
                          </th>
                          <th className='px-6 py-3 text-left text-xs font-medium uppercase'>
                            Delivery Rate
                          </th>
                          <th className='px-6 py-3 text-left text-xs font-medium uppercase'>
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className='divide-y divide-gray-200'>
                        {renderBreakdownTable()}
                      </tbody>
                    </table>
                  </div>
                </section>
              </>
            )}
          </div>
        </div>
      )}
      <EmailListModal
        messages={selected}
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({ loading: state?.app?.visible ?? false });

export default connect(mapStateToProps, { getEmailAnalytics })(EmailAnalytics);
